import {useQueryClient} from '@tanstack/react-query';
import React, {useCallback} from 'react';
import {Stack} from '@mui/material';
import {Button, Headline, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import MessagesTab from '@/components/creator-side-panel/components/MessagesTab';
import SideDrawer from '@/components/side-drawer';
import {CONVERSATION_DETAILS_QUERY_KEY} from '@/hooks/queries/use-conversation-details-query/useConversationDetailsQuery';
import styles from './MessagesSidePanel.module.scss';

type MessagesSidePanelProps = {
  isOpen: boolean;
  onClose: () => void;
  creatorName: string;
  conversationId: string | null;
  brandId: string;
  brandName: string;
  campaignName?: string;
  campaignId?: string;
  testID?: string;
};

function MessagesSidePanel({
  isOpen,
  onClose,
  creatorName,
  conversationId,
  brandId,
  brandName,
  campaignName,
  campaignId,
  testID = 'messages-side-panel',
}: MessagesSidePanelProps) {
  const queryClient = useQueryClient();

  // Option C: invalidate conversation details on close so unread indicators
  // refresh on next open, without clearing them while the user is reading.
  const handleClose = useCallback(() => {
    if (conversationId && brandId) {
      queryClient.invalidateQueries({
        queryKey: [CONVERSATION_DETAILS_QUERY_KEY, conversationId, brandId],
      });
    }
    onClose();
  }, [onClose, queryClient, conversationId, brandId]);

  return (
    <SideDrawer
      testID={testID}
      isOpen={isOpen}
      onClose={handleClose}
      withoutBackdrop
      drawerClassName={styles.sideDrawer}
    >
      <Stack className={styles.container}>
        <div className={styles.header}>
          <Headline size="sm" className={styles.title}>
            {translate('message_creator_name', {creatorName})}
          </Headline>
          <Button
            className={styles.closeButton}
            size="small"
            appearance="neutral"
            mode="plain"
            onClick={handleClose}
            aria-label={translate('Close')}
          >
            <Icon
              size="small"
              appearance="neutral"
              name="Actions-Close-Normal"
            />
          </Button>
        </div>
        <MessagesTab
          conversationId={conversationId ?? ''}
          brandId={brandId}
          brandName={brandName}
          creatorName={creatorName}
          campaignName={campaignName}
          campaignId={campaignId}
        />
      </Stack>
    </SideDrawer>
  );
}

export default MessagesSidePanel;
